import type { LocaleModule } from '../types';

export interface CommonMessages {
  loading: string;
  appName: string;
  confirm: string;
  cancel: string;
  save: string;
  testVersionNotice: string;
  errorBoundaryTitle: string;
  retry: string;
  unknownError: string;
  errorDetails: string;
  refreshPage: string;
  errorNetworkConnection: string;
  errorBadRequest: string;
  errorUnauthorized: string;
  errorForbidden: string;
  errorServiceUnavailable: string;
  errorServer: string;
  errorDeviceConnection: string;
  errorDeviceNotFound: string;
  errorDeviceOffline: string;
  errorDeviceInUse: string;
  errorAudioInitialization: string;
  errorMicrophonePermission: string;
  errorAudioUnsupported: string;
  errorMicrophoneNotFound: string;
  errorFrequencySetting: string;
  errorModeSetting: string;
  errorSpectrumConnection: string;
  errorRequestTimeout: string;
  errorContextFailed: string;
  errorContextOperation: string;
  errorContextDeviceConnection: string;
  errorContextDeviceUsage: string;
  errorContextAudioSystem: string;
  errorContextFrequencySetting: string;
  errorContextModeSetting: string;
  errorContextSpectrumAnalysis: string;
  errorContextPttOperation: string;
  unnamed: string;
  requestFailed: string;
}

export const commonLocales: LocaleModule<CommonMessages> = {
  zh_CN: {
    loading: '加载中...',
    appName: 'WaveLink',
    confirm: '确认',
    cancel: '取消',
    save: '保存',
    testVersionNotice: '当前版本为测试版本，供大家免费使用',
    errorBoundaryTitle: '出现错误',
    retry: '重试',
    unknownError: '发生了未知错误',
    errorDetails: '错误详情',
    refreshPage: '刷新页面',
    errorNetworkConnection: '网络连接失败，请检查网络设置后重试',
    errorBadRequest: '请求参数错误，请检查输入信息',
    errorUnauthorized: '认证失败，请重新登录',
    errorForbidden: '权限不足，无法执行此操作',
    errorServiceUnavailable: '服务暂时不可用，请稍后重试',
    errorServer: '服务器内部错误，请稍后重试',
    errorDeviceConnection: '设备连接失败，请检查设备状态后重试',
    errorDeviceNotFound: '设备不存在或未注册，请检查设备信息',
    errorDeviceOffline: '设备当前离线，请等待设备上线后重试',
    errorDeviceInUse: '设备正在被其他用户使用，请稍后重试',
    errorAudioInitialization: '音频设备初始化失败，请检查麦克风权限设置',
    errorMicrophonePermission: '麦克风权限被拒绝，请在浏览器设置中允许麦克风访问',
    errorAudioUnsupported: '当前浏览器不支持音频功能，请使用现代浏览器',
    errorMicrophoneNotFound: '未检测到麦克风设备，请检查设备连接',
    errorFrequencySetting: '频率设置失败，请检查设备连接状态',
    errorModeSetting: '模式设置失败，请检查设备连接状态',
    errorSpectrumConnection: '频谱分析连接失败，请检查网络连接',
    errorRequestTimeout: '请求超时，请检查网络连接后重试',
    errorContextFailed: '{context}失败，请稍后重试',
    errorContextOperation: '操作',
    errorContextDeviceConnection: '设备连接',
    errorContextDeviceUsage: '设备使用',
    errorContextAudioSystem: '音频系统',
    errorContextFrequencySetting: '频率设置',
    errorContextModeSetting: '模式设置',
    errorContextSpectrumAnalysis: '频谱分析',
    errorContextPttOperation: 'PTT操作',
    unnamed: '未命名',
    requestFailed: '请求失败',
  },
  zh_HK: {
    loading: '載入中...',
    appName: 'WaveLink',
    confirm: '確認',
    cancel: '取消',
    save: '儲存',
    testVersionNotice: '目前版本為測試版本，供大家免費使用',
    errorBoundaryTitle: '發生錯誤',
    retry: '重試',
    unknownError: '發生未知錯誤',
    errorDetails: '錯誤詳情',
    refreshPage: '重新整理頁面',
    errorNetworkConnection: '網絡連線失敗，請檢查網絡設定後重試',
    errorBadRequest: '請求參數錯誤，請檢查輸入資料',
    errorUnauthorized: '驗證失敗，請重新登入',
    errorForbidden: '權限不足，無法執行此操作',
    errorServiceUnavailable: '服務暫時無法使用，請稍後重試',
    errorServer: '伺服器內部錯誤，請稍後重試',
    errorDeviceConnection: '裝置連線失敗，請檢查裝置狀態後重試',
    errorDeviceNotFound: '裝置不存在或尚未註冊，請檢查裝置資料',
    errorDeviceOffline: '裝置目前離線，請等待裝置上線後重試',
    errorDeviceInUse: '裝置正由其他使用者使用，請稍後重試',
    errorAudioInitialization: '音訊裝置初始化失敗，請檢查麥克風權限設定',
    errorMicrophonePermission: '麥克風權限遭拒，請在瀏覽器設定中允許存取麥克風',
    errorAudioUnsupported: '目前的瀏覽器不支援音訊功能，請使用較新的瀏覽器',
    errorMicrophoneNotFound: '偵測不到麥克風裝置，請檢查裝置連線',
    errorFrequencySetting: '頻率設定失敗，請檢查裝置連線狀態',
    errorModeSetting: '模式設定失敗，請檢查裝置連線狀態',
    errorSpectrumConnection: '頻譜分析連線失敗，請檢查網絡連線',
    errorRequestTimeout: '請求逾時，請檢查網絡連線後重試',
    errorContextFailed: '{context}失敗，請稍後重試',
    errorContextOperation: '操作',
    errorContextDeviceConnection: '裝置連線',
    errorContextDeviceUsage: '裝置使用',
    errorContextAudioSystem: '音訊系統',
    errorContextFrequencySetting: '頻率設定',
    errorContextModeSetting: '模式設定',
    errorContextSpectrumAnalysis: '頻譜分析',
    errorContextPttOperation: 'PTT 操作',
    unnamed: '未命名',
    requestFailed: '請求失敗',
  },
  en_US: {
    loading: 'Loading...',
    appName: 'WaveLink',
    confirm: 'Confirm',
    cancel: 'Cancel',
    save: 'Save',
    testVersionNotice: 'This is a test version and is free to use',
    errorBoundaryTitle: 'Something went wrong',
    retry: 'Try again',
    unknownError: 'An unknown error occurred',
    errorDetails: 'Error details',
    refreshPage: 'Refresh page',
    errorNetworkConnection: 'Could not connect to the network. Check your network settings and try again.',
    errorBadRequest: 'The request is invalid. Check the information you entered.',
    errorUnauthorized: 'Authentication failed. Please sign in again.',
    errorForbidden: 'You do not have permission to perform this action.',
    errorServiceUnavailable: 'The service is temporarily unavailable. Please try again later.',
    errorServer: 'An internal server error occurred. Please try again later.',
    errorDeviceConnection: 'Could not connect to the device. Check its status and try again.',
    errorDeviceNotFound: 'The device does not exist or is not registered. Check the device information.',
    errorDeviceOffline: 'The device is offline. Wait for it to come online and try again.',
    errorDeviceInUse: 'The device is being used by another user. Please try again later.',
    errorAudioInitialization: 'Could not initialize the audio device. Check your microphone permissions.',
    errorMicrophonePermission: 'Microphone access was denied. Allow microphone access in your browser settings.',
    errorAudioUnsupported: 'This browser does not support audio features. Please use a modern browser.',
    errorMicrophoneNotFound: 'No microphone was detected. Check that it is connected.',
    errorFrequencySetting: 'Could not set the frequency. Check the device connection.',
    errorModeSetting: 'Could not set the mode. Check the device connection.',
    errorSpectrumConnection: 'Could not connect to spectrum analysis. Check your network connection.',
    errorRequestTimeout: 'The request timed out. Check your network connection and try again.',
    errorContextFailed: '{context} failed. Please try again later.',
    errorContextOperation: 'Operation',
    errorContextDeviceConnection: 'Device connection',
    errorContextDeviceUsage: 'Device operation',
    errorContextAudioSystem: 'Audio system',
    errorContextFrequencySetting: 'Frequency setup',
    errorContextModeSetting: 'Mode setup',
    errorContextSpectrumAnalysis: 'Spectrum analysis',
    errorContextPttOperation: 'PTT operation',
    unnamed: 'Unnamed',
    requestFailed: 'Request failed',
  },
  ru_RU: {
    loading: 'Загрузка...',
    appName: 'WaveLink',
    confirm: 'Подтвердить',
    cancel: 'Отмена',
    save: 'Сохранить',
    testVersionNotice: 'Это тестовая версия, доступная для бесплатного использования',
    errorBoundaryTitle: 'Произошла ошибка',
    retry: 'Повторить',
    unknownError: 'Произошла неизвестная ошибка',
    errorDetails: 'Подробности ошибки',
    refreshPage: 'Обновить страницу',
    errorNetworkConnection: 'Не удалось подключиться к сети. Проверьте настройки сети и повторите попытку.',
    errorBadRequest: 'Неверные параметры запроса. Проверьте введённые данные.',
    errorUnauthorized: 'Ошибка аутентификации. Войдите снова.',
    errorForbidden: 'Недостаточно прав для выполнения этого действия.',
    errorServiceUnavailable: 'Сервис временно недоступен. Повторите попытку позже.',
    errorServer: 'Внутренняя ошибка сервера. Повторите попытку позже.',
    errorDeviceConnection: 'Не удалось подключиться к устройству. Проверьте его состояние и повторите попытку.',
    errorDeviceNotFound: 'Устройство не существует или не зарегистрировано. Проверьте данные устройства.',
    errorDeviceOffline: 'Устройство не в сети. Дождитесь подключения и повторите попытку.',
    errorDeviceInUse: 'Устройство используется другим пользователем. Повторите попытку позже.',
    errorAudioInitialization: 'Не удалось инициализировать аудиоустройство. Проверьте разрешения микрофона.',
    errorMicrophonePermission: 'Доступ к микрофону запрещён. Разрешите его в настройках браузера.',
    errorAudioUnsupported: 'Этот браузер не поддерживает аудиофункции. Используйте современный браузер.',
    errorMicrophoneNotFound: 'Микрофон не обнаружен. Проверьте подключение устройства.',
    errorFrequencySetting: 'Не удалось настроить частоту. Проверьте подключение устройства.',
    errorModeSetting: 'Не удалось настроить режим. Проверьте подключение устройства.',
    errorSpectrumConnection: 'Не удалось подключиться к анализатору спектра. Проверьте сетевое подключение.',
    errorRequestTimeout: 'Время ожидания запроса истекло. Проверьте сеть и повторите попытку.',
    errorContextFailed: 'Не удалось выполнить действие «{context}». Повторите попытку позже.',
    errorContextOperation: 'Операция',
    errorContextDeviceConnection: 'Подключение устройства',
    errorContextDeviceUsage: 'Использование устройства',
    errorContextAudioSystem: 'Аудиосистема',
    errorContextFrequencySetting: 'Настройка частоты',
    errorContextModeSetting: 'Настройка режима',
    errorContextSpectrumAnalysis: 'Анализ спектра',
    errorContextPttOperation: 'Операция PTT',
    unnamed: 'Без имени',
    requestFailed: 'Ошибка запроса',
  },
  es_ES: {
    loading: 'Cargando...',
    appName: 'WaveLink',
    confirm: 'Confirmar',
    cancel: 'Cancelar',
    save: 'Guardar',
    testVersionNotice: 'Esta es una versión de prueba disponible de forma gratuita',
    errorBoundaryTitle: 'Se ha producido un error',
    retry: 'Reintentar',
    unknownError: 'Se ha producido un error desconocido',
    errorDetails: 'Detalles del error',
    refreshPage: 'Actualizar página',
    errorNetworkConnection: 'No se pudo conectar a la red. Comprueba la configuración de red e inténtalo de nuevo.',
    errorBadRequest: 'Los parámetros de la solicitud no son válidos. Comprueba los datos introducidos.',
    errorUnauthorized: 'Error de autenticación. Vuelve a iniciar sesión.',
    errorForbidden: 'No tienes permiso para realizar esta acción.',
    errorServiceUnavailable: 'El servicio no está disponible temporalmente. Inténtalo de nuevo más tarde.',
    errorServer: 'Se ha producido un error interno del servidor. Inténtalo de nuevo más tarde.',
    errorDeviceConnection: 'No se pudo conectar al dispositivo. Comprueba su estado e inténtalo de nuevo.',
    errorDeviceNotFound: 'El dispositivo no existe o no está registrado. Comprueba sus datos.',
    errorDeviceOffline: 'El dispositivo está desconectado. Espera a que se conecte e inténtalo de nuevo.',
    errorDeviceInUse: 'Otro usuario está utilizando el dispositivo. Inténtalo de nuevo más tarde.',
    errorAudioInitialization: 'No se pudo inicializar el dispositivo de audio. Comprueba los permisos del micrófono.',
    errorMicrophonePermission: 'Se denegó el acceso al micrófono. Permítelo en la configuración del navegador.',
    errorAudioUnsupported: 'Este navegador no admite funciones de audio. Utiliza un navegador moderno.',
    errorMicrophoneNotFound: 'No se detectó ningún micrófono. Comprueba que esté conectado.',
    errorFrequencySetting: 'No se pudo configurar la frecuencia. Comprueba la conexión del dispositivo.',
    errorModeSetting: 'No se pudo configurar el modo. Comprueba la conexión del dispositivo.',
    errorSpectrumConnection: 'No se pudo conectar al análisis de espectro. Comprueba la conexión de red.',
    errorRequestTimeout: 'La solicitud agotó el tiempo de espera. Comprueba la red e inténtalo de nuevo.',
    errorContextFailed: 'No se pudo completar: {context}. Inténtalo de nuevo más tarde.',
    errorContextOperation: 'Operación',
    errorContextDeviceConnection: 'Conexión del dispositivo',
    errorContextDeviceUsage: 'Uso del dispositivo',
    errorContextAudioSystem: 'Sistema de audio',
    errorContextFrequencySetting: 'Configuración de frecuencia',
    errorContextModeSetting: 'Configuración de modo',
    errorContextSpectrumAnalysis: 'Análisis de espectro',
    errorContextPttOperation: 'Operación PTT',
    unnamed: 'Sin nombre',
    requestFailed: 'Error en la solicitud',
  },
  fr_FR: {
    loading: 'Chargement...',
    appName: 'WaveLink',
    confirm: 'Confirmer',
    cancel: 'Annuler',
    save: 'Enregistrer',
    testVersionNotice: 'Ceci est une version de test disponible gratuitement',
    errorBoundaryTitle: 'Une erreur est survenue',
    retry: 'Réessayer',
    unknownError: 'Une erreur inconnue est survenue',
    errorDetails: 'Détails de l’erreur',
    refreshPage: 'Actualiser la page',
    errorNetworkConnection: 'Impossible de se connecter au réseau. Vérifiez vos paramètres réseau et réessayez.',
    errorBadRequest: 'Les paramètres de la requête sont incorrects. Vérifiez les informations saisies.',
    errorUnauthorized: 'Échec de l’authentification. Veuillez vous reconnecter.',
    errorForbidden: 'Vous n’avez pas l’autorisation d’effectuer cette action.',
    errorServiceUnavailable: 'Le service est temporairement indisponible. Réessayez plus tard.',
    errorServer: 'Une erreur interne du serveur est survenue. Réessayez plus tard.',
    errorDeviceConnection: 'Impossible de se connecter à l’appareil. Vérifiez son état et réessayez.',
    errorDeviceNotFound: 'L’appareil n’existe pas ou n’est pas enregistré. Vérifiez ses informations.',
    errorDeviceOffline: 'L’appareil est hors ligne. Attendez qu’il se reconnecte et réessayez.',
    errorDeviceInUse: 'L’appareil est utilisé par un autre utilisateur. Réessayez plus tard.',
    errorAudioInitialization: 'Impossible d’initialiser l’appareil audio. Vérifiez les autorisations du microphone.',
    errorMicrophonePermission: 'L’accès au microphone a été refusé. Autorisez-le dans les paramètres du navigateur.',
    errorAudioUnsupported: 'Ce navigateur ne prend pas en charge les fonctions audio. Utilisez un navigateur récent.',
    errorMicrophoneNotFound: 'Aucun microphone n’a été détecté. Vérifiez qu’il est connecté.',
    errorFrequencySetting: 'Impossible de régler la fréquence. Vérifiez la connexion de l’appareil.',
    errorModeSetting: 'Impossible de régler le mode. Vérifiez la connexion de l’appareil.',
    errorSpectrumConnection: 'Impossible de se connecter à l’analyseur de spectre. Vérifiez votre connexion réseau.',
    errorRequestTimeout: 'La requête a expiré. Vérifiez votre connexion réseau et réessayez.',
    errorContextFailed: 'Échec de l’opération « {context} ». Réessayez plus tard.',
    errorContextOperation: 'Opération',
    errorContextDeviceConnection: 'Connexion de l’appareil',
    errorContextDeviceUsage: 'Utilisation de l’appareil',
    errorContextAudioSystem: 'Système audio',
    errorContextFrequencySetting: 'Réglage de la fréquence',
    errorContextModeSetting: 'Réglage du mode',
    errorContextSpectrumAnalysis: 'Analyse du spectre',
    errorContextPttOperation: 'Opération PTT',
    unnamed: 'Sans nom',
    requestFailed: 'Échec de la requête',
  },
  ja_JP: {
    loading: '読み込み中...',
    appName: 'WaveLink',
    confirm: '確認',
    cancel: 'キャンセル',
    save: '保存',
    testVersionNotice: '現在のバージョンは無料でご利用いただけるテスト版です',
    errorBoundaryTitle: 'エラーが発生しました',
    retry: '再試行',
    unknownError: '不明なエラーが発生しました',
    errorDetails: 'エラーの詳細',
    refreshPage: 'ページを更新',
    errorNetworkConnection: 'ネットワークに接続できませんでした。ネットワーク設定を確認して再試行してください。',
    errorBadRequest: 'リクエストのパラメーターが正しくありません。入力内容を確認してください。',
    errorUnauthorized: '認証に失敗しました。もう一度ログインしてください。',
    errorForbidden: 'この操作を実行する権限がありません。',
    errorServiceUnavailable: 'サービスは一時的に利用できません。しばらくしてから再試行してください。',
    errorServer: 'サーバー内部エラーが発生しました。しばらくしてから再試行してください。',
    errorDeviceConnection: 'デバイスに接続できませんでした。デバイスの状態を確認して再試行してください。',
    errorDeviceNotFound: 'デバイスが存在しないか、登録されていません。デバイス情報を確認してください。',
    errorDeviceOffline: 'デバイスはオフラインです。オンラインになってから再試行してください。',
    errorDeviceInUse: 'デバイスは別のユーザーが使用中です。しばらくしてから再試行してください。',
    errorAudioInitialization: 'オーディオデバイスを初期化できませんでした。マイクの権限設定を確認してください。',
    errorMicrophonePermission: 'マイクへのアクセスが拒否されました。ブラウザーの設定でアクセスを許可してください。',
    errorAudioUnsupported: 'このブラウザーはオーディオ機能に対応していません。最新のブラウザーをお使いください。',
    errorMicrophoneNotFound: 'マイクが検出されませんでした。デバイスの接続を確認してください。',
    errorFrequencySetting: '周波数を設定できませんでした。デバイスの接続を確認してください。',
    errorModeSetting: 'モードを設定できませんでした。デバイスの接続を確認してください。',
    errorSpectrumConnection: 'スペクトラム解析に接続できませんでした。ネットワーク接続を確認してください。',
    errorRequestTimeout: 'リクエストがタイムアウトしました。ネットワーク接続を確認して再試行してください。',
    errorContextFailed: '{context}に失敗しました。しばらくしてから再試行してください。',
    errorContextOperation: '操作',
    errorContextDeviceConnection: 'デバイス接続',
    errorContextDeviceUsage: 'デバイス操作',
    errorContextAudioSystem: 'オーディオシステム',
    errorContextFrequencySetting: '周波数設定',
    errorContextModeSetting: 'モード設定',
    errorContextSpectrumAnalysis: 'スペクトラム解析',
    errorContextPttOperation: 'PTT 操作',
    unnamed: '名前なし',
    requestFailed: 'リクエストに失敗しました',
  },
};

export type CommonLocaleKey = keyof CommonMessages;
